import { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "react-native";
import { useDispatch, useStore } from "react-redux";
import * as Sentry from "@sentry/react-native";
import uuid from "react-native-uuid";
import { openSettings } from "react-native-permissions";
import {
  isAlreadyRunningError,
  isCameraPermissionBlockedError,
  isCameraPermissionError,
  isFaceCaptureAvailable,
  isNoActivityError,
  launchFaceCapture,
} from "react-native-face-capture";
import type { FaceCaptureConfig } from "react-native-face-capture";
import { appVersion, appVersionCode, baseURL } from "@config";
import { identifyAttendanceSubject } from "@services/api";
import { actions as attendanceActions } from "@reducers/attendanceReducer";
import { actions as locationActions } from "@reducers/locationReducer";
import useCachedLocation from "@hooks/useCachedLocation";
import { attendancePendingEventSelector } from "@selectors/attendance.selector";
import getCurrentLocation from "@utils/getCurrentLocation";
import toast from "@utils/toast";
import {
  buildAttendanceLaunchConfig,
  isSubjectTokenStale,
  pendingEventKey,
  todayIST,
} from "@utils/attendance";
import type {
  ApiErrorBody,
  AttendanceRole,
  CaptureResponse,
  EventType,
  IdentifyResponse,
} from "./types";

interface Session {
  identity: IdentifyResponse;
  phone: string;
  issuedAt: number;
}

interface RootStateLike {
  auth?: { userToken?: string | null };
}

interface Options {
  session: Session;
  onSessionRefreshed: (identity: IdentifyResponse, issuedAt: number) => void;
  /** `null` when the response body could not be read — the caller should re-fetch. */
  onUploaded: (response: CaptureResponse | null) => void;
}

const NO_ACTIVITY_RETRY_MS = 1000;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

interface Fix {
  lat: number;
  lng: number;
  accuracy: number | null;
}

const isCaptureResponse = (value: unknown): value is CaptureResponse =>
  typeof value === "object" &&
  value !== null &&
  ("in" in value || "out" in value);

/**
 * Drives one IN/OUT mark through the native react-native-face-capture screen.
 *
 * - Location comes from the session cache (useCachedLocation, the delivery-boy
 *   pattern): read synchronously at tap time, so the camera never waits on
 *   GPS. The page warms the cache on open when it is empty.
 *
 * - Re-identifies silently first when the subject token is close to expiring
 *   (the upload would otherwise fail with SUBJECT_TOKEN_EXPIRED under the SDK).
 * - Persists client_event_id BEFORE launch and reuses it on relaunch; cleared
 *   after `uploaded`.
 */
export const useAttendanceCapture = ({
  session,
  onSessionRefreshed,
  onUploaded,
}: Options) => {
  const dispatch = useDispatch();
  const store = useStore<RootStateLike>();
  const [busyEvent, setBusyEvent] = useState<EventType | null>(null);
  const running = useRef(false);
  const getCachedLocation = useCachedLocation();
  const warming = useRef(false);

  /**
   * Fill the session location cache when it is empty, in the background: one
   * one-shot capture published to the `location` slice (the tracking stream
   * keeps it fresh after that). Never awaited by a mark.
   */
  const warmLocation = useCallback(() => {
    if (warming.current || getCachedLocation().latitude != null) return;
    warming.current = true;
    getCurrentLocation({ timeoutMs: 10000 })
      .then(({ coords }) => {
        dispatch(
          locationActions.locationFixReceived({
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            source: "capture",
          }),
        );
      })
      .catch(() => undefined)
      .finally(() => {
        warming.current = false;
      });
  }, [dispatch, getCachedLocation]);

  // Warm the cache as soon as the attendance page opens.
  useEffect(() => {
    warmLocation();
  }, [warmLocation]);

  const ensureFreshIdentity =
    useCallback(async (): Promise<IdentifyResponse | null> => {
      if (!isSubjectTokenStale(session.issuedAt)) return session.identity;
      const res = await identifyAttendanceSubject({
        role: session.identity.role as AttendanceRole,
        phone: session.phone,
      });
      if (res?.status === 200 && res.data?.subject_token) {
        const fresh = res.data as IdentifyResponse;
        onSessionRefreshed(fresh, Date.now());
        return fresh;
      }
      const body = (res?.data ?? {}) as ApiErrorBody;
      toast.showLongToast(
        body.message || "Could not verify the number. Try again.",
      );
      return null;
    }, [session, onSessionRefreshed]);

  const mark = useCallback(
    async (eventType: EventType) => {
      if (running.current) return;
      running.current = true;
      setBusyEvent(eventType);
      try {
        if (!isFaceCaptureAvailable()) {
          Sentry.captureMessage(
            "attendance: FaceCaptureModule missing",
            "warning",
          );
          toast.showLongToast("Please update the app to mark attendance.");
          return;
        }
        const identity = await ensureFreshIdentity();
        const authToken = store.getState().auth?.userToken;
        if (!identity || !authToken) return;

        // A refresh can reveal the event was already marked (e.g. from
        // another phone); don't make them take a selfie the server will 409.
        const alreadyMarked = eventType === "IN" ? identity.in : identity.out;
        if (alreadyMarked) {
          toast.showLongToast("Attendance is already marked for today.");
          return;
        }

        const key = pendingEventKey({
          role: identity.role,
          displayId: identity.subject.display_id,
          date: identity.date || todayIST(),
          eventType,
        });
        let clientEventId = attendancePendingEventSelector(key)(
          store.getState(),
        );
        if (!clientEventId) {
          clientEventId = String(uuid.v4());
          dispatch(
            attendanceActions.eventStarted({
              key,
              clientEventId,
              today: todayIST(),
            }),
          );
        }

        // Coordinates are for reporting; a mark is never withheld over them.
        const cached = getCachedLocation();
        const fix: Fix | undefined =
          cached.latitude != null && cached.longitude != null
            ? {
                lat: cached.latitude,
                lng: cached.longitude,
                accuracy: cached.accuracy,
              }
            : undefined;

        const launch = () =>
          launchFaceCapture(
            buildAttendanceLaunchConfig({
              eventType,
              subjectToken: identity.subject_token,
              token: authToken,
              baseUrl: baseURL,
              appVersion,
              appVersionCode,
              clientEventId: clientEventId as string,
              fix,
              capturedAt: new Date().toISOString(),
            }) as FaceCaptureConfig,
          );

        let result;
        try {
          result = await launch();
        } catch (error) {
          if (!isNoActivityError(error)) throw error;
          await sleep(NO_ACTIVITY_RETRY_MS);
          result = await launch();
        }

        if (result.status === "uploaded") {
          dispatch(attendanceActions.eventCleared(key));
          const json = result.response?.json;
          onUploaded(isCaptureResponse(json) ? json : null);
        }
      } catch (error) {
        if (isCameraPermissionBlockedError(error)) {
          Alert.alert(
            "Camera permission required",
            "Turn on the camera in Settings to mark attendance.",
            [
              { text: "Later", style: "cancel" },
              {
                text: "Open settings",
                onPress: () => {
                  openSettings().catch(() => undefined);
                },
              },
            ],
          );
        } else if (isCameraPermissionError(error)) {
          toast.showLongToast("Allow camera access to mark attendance.");
        } else if (isAlreadyRunningError(error)) {
          // A capture is already open on screen: nothing to add.
        } else {
          Sentry.captureException(error);
          toast.showLongToast("Could not open the camera. Try again.");
        }
      } finally {
        running.current = false;
        setBusyEvent(null);
        // Nothing cached yet (no tracking, capture still pending)? Try again
        // for the next mark.
        warmLocation();
      }
    },
    [
      dispatch,
      store,
      ensureFreshIdentity,
      getCachedLocation,
      warmLocation,
      onUploaded,
    ],
  );

  return { mark, busy: busyEvent !== null, busyEvent };
};
